import {flatMap} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CenteredPopover} from '../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {PopoverProps} from '../../../libs/shared/ui/src/components/popovers/popoverTypes';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {CarouselBreadcrumbs} from './carouselBreadcrumbs';
import {addSlideGridContainerStyle, CarouselSlideDefinition, CarouselSlideFooterLeft} from './carouselCommon';
import {CarouselSlide} from './carouselSlide';

/*
 * Props.
 */

interface CarouselProps extends PopoverProps {
  slides: ReadonlyArray<CarouselSlideDefinition>;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledCarouselDiv = styled.div`
  width: 800px;
  height: 451px;
  overflow: hidden;

  ${addSlideGridContainerStyle()}

  border-radius: 24px;
  background-color: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 25px 65px 0 ${p.theme.alphas.black30}`)};
  position: relative;
`;

/*
 * Component.
 */

export const Carousel: FC<CarouselProps> = (props) => {
  const {slides, onRequestClose} = props;
  const slidesCount = slides.length;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  /*
   * Event handlers.
   */

  const onPreviousClick = useCallback(() => {
    setCurrentSlideIndex((value) => (value === 0 ? value : value - 1));
  }, []);

  const onNextClick = useCallback(() => {
    // If it's the last slide, clicking Next should close the carousel.
    if (currentSlideIndex === slidesCount - 1) {
      onRequestClose();
    } else {
      setCurrentSlideIndex((value) => (value >= slidesCount - 1 ? value : value + 1));
    }
  }, [currentSlideIndex, slidesCount, onRequestClose]);

  /*
   * Lifecycle.
   */

  // Preload the images for the slides on load.
  useEffect(() => {
    preloadSlideImagesAsync(slides);
  }, [slides]);

  // Current slide.
  const slideDefinition = slides[currentSlideIndex];

  // Only show breadcrumbs if we have more than 1 slide, and the slide hasn't explicitely hidden them.
  const shouldShowBreadcrumbs = slides.length > 1 && !slideDefinition.shouldHideBreadcrumbs;

  /*
   * Render.
   */

  return (
    <CenteredPopover isExclusive>
      <StyledCarouselDiv>
        <CarouselSlide
          index={currentSlideIndex}
          slide={slideDefinition}
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
        />
        <CarouselSlideFooterLeft>
          {shouldShowBreadcrumbs && (
            <CarouselBreadcrumbs count={slidesCount} selectedIndex={currentSlideIndex} />
          )}
        </CarouselSlideFooterLeft>
      </StyledCarouselDiv>
    </CenteredPopover>
  );
};

/*
 * Helpers.
 */

async function preloadSlideImagesAsync(slides: ReadonlyArray<CarouselSlideDefinition>) {
  const imageUrls = flatMap(slides, (slide) => slide.imageUrls || []);

  // Preload them in sequence.
  for (const imageUrl of imageUrls) // eslint-disable-line
    await preloadImageAsync(imageUrl); // eslint-disable-line
}

function preloadImageAsync(imageUrl: string) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(undefined);
    image.onerror = () => resolve(undefined);
    image.src = imageUrl;
  });
}
